import {
  ANTECEDENCIA_MAXIMA_EM_DIAS,
  criarRegraDeAntecedenciaSchema,
  criarRegraDeDiaDaSemanaSchema,
  criarRegraDeOcupacaoSchema,
  criarSazonalidadeSchema,
  DIAS_DA_SEMANA,
  DIAS_DA_SEMANA_VALIDOS,
  type EfeitoDeRegra,
  EFEITOS_DE_REGRA,
  MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE,
  PONTOS_BASE_NEUTROS,
  PRIORIDADE_MAXIMA,
  ROTULO_DO_DIA,
  ROTULO_DO_EFEITO,
  ROTULO_DO_TIPO_DE_LINHA,
  type TipoDeLinha,
} from '@locaveri/shared'
import { Banknote, LoaderCircle, Percent } from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
// A categoria é da FROTA — é ela o produto que o cliente aluga. A regra de
// preço só a referencia, e por isso o seletor lê a lista de lá em vez de
// duplicar um schema de categoria dentro de preços.
import { useCategoriasAtivas } from '@/features/frota/api'
import { SEM_VALOR } from '@/lib/datas'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { cn } from '@/lib/utils'

import {
  escopoDaRegra,
  formatarMultiplicador,
  formatarPontosBaseComoPercentual,
  formatarVariacaoDoMultiplicador,
  type RegraDeAntecedencia,
  type RegraDeDiaDaSemana,
  type RegraDeOcupacao,
  type Sazonalidade,
  useAtualizarRegraDeAntecedencia,
  useAtualizarRegraDeDiaDaSemana,
  useAtualizarRegraDeOcupacao,
  useAtualizarSazonalidade,
  useCriarRegraDeAntecedencia,
  useCriarRegraDeDiaDaSemana,
  useCriarRegraDeOcupacao,
  useCriarSazonalidade,
  usePoliticaDeLocacao,
  useTabelasAtivas,
} from './api'

/**
 * O formulário das quatro camadas de regra de preço.
 *
 * ---------------------------------------------------------------------------
 * UM DIÁLOGO, QUATRO CAMADAS — E POR QUE NÃO QUATRO ARQUIVOS
 * ---------------------------------------------------------------------------
 * As quatro regras são a MESMA frase de negócio dita sobre eixos diferentes:
 * "neste recorte, o preço da faixa vira outra coisa". Nome, escopo (categoria e
 * tabela), multiplicador e interruptor de ativo são idênticos nas quatro; o que
 * muda é o recorte — período, dia da semana, faixa de antecedência, faixa de
 * ocupação. Quatro arquivos seriam quatro cópias do mesmo cabeçalho, do mesmo
 * reset, do mesmo tratamento de erro, e a quinta camada nasceria divergindo.
 *
 * ---------------------------------------------------------------------------
 * FIXO SUBSTITUI, MULTIPLICADOR ESCALA
 * ---------------------------------------------------------------------------
 * A diferença muda o preço em ordem de grandeza, e é por isso que ela está
 * escrita na tela e não só aqui: `MULTIPLICADOR` pega a diária da faixa e a
 * escala (1,25× sobre R$ 900 = R$ 1.125); `FIXO` DESCARTA a diária da faixa e
 * manda o valor — "no Réveillon a diária é R$ 1.200, ponto". Quem confunde os
 * dois digita 1200 num campo de multiplicador e cria uma diária de R$ 1,08
 * milhão, ou digita 1,25 no campo de valor fixo e vende o Réveillon por um
 * centavo.
 *
 * ---------------------------------------------------------------------------
 * PONTOS-BASE NA API, FATOR NA TELA — ONDE O BUG DE FATOR 100 NASCE
 * ---------------------------------------------------------------------------
 * A API guarda multiplicador em PONTOS-BASE inteiros (`12000` = 1,2×) porque
 * `1.2` não é representável em binário e uma taxa que "quase" fecha vira
 * centavo perdido multiplicado por toda diária de toda reserva. A pessoa, por
 * outro lado, pensa em "1,25×" ou "25% mais caro".
 *
 * Então a conversão acontece NESTE arquivo, nos dois sentidos, em um lugar só —
 * `fatorParaPontosBase` e `pontosBaseParaCampoDeFator`, com a leitura formatada
 * por `formatarMultiplicador`/`formatarVariacaoDoMultiplicador` de `./api`. Ela
 * é feita sobre os DÍGITOS da string, nunca com `Number(texto) * 10000`:
 * `1.15 * 10000` em ponto flutuante dá 11499.999999999998, e um `Math.round`
 * esquecido aqui viraria um multiplicador errado aplicado a toda locação do
 * recorte, sem ninguém perceber. O campo mostra, embaixo, o número exato que
 * vai para a API — é a conferência que impede o fator 100 de passar.
 *
 * A ocupação usa a MESMA escala em outro papel — e é aí que mora a segunda
 * armadilha de fator 100. Multiplicador tem QUATRO casas (10000 = 1×, 12000 =
 * 1,2×); percentual de ocupação tem DUAS (10000 = 100%, 1500 = 15%), como diz o
 * `percentualSchema` do `@locaveri/shared`. São duas conversões diferentes
 * porque são duas escalas diferentes, e trocá-las daria uma faixa de ocupação
 * cem vezes menor do que a digitada. Por isso `fatorParaPontosBase` e
 * `percentualParaPontosBase` são funções separadas, com constantes de casas
 * separadas, e as duas mostram embaixo do campo o inteiro que vai viajar.
 *
 * ---------------------------------------------------------------------------
 * O QUE VIAJA É A ENTRADA, NÃO O `validado.data`
 * ---------------------------------------------------------------------------
 * `criarSazonalidadeSchema` transforma `valorFixo` em `bigint` (invariante nº 1)
 * e `JSON.stringify` de um `bigint` LANÇA. O `safeParse` roda para VALIDAR; o
 * que vai para o `fetch` é a entrada validada — strings e números.
 *
 * Os schemas de CRIAÇÃO validam também a edição, de propósito: o formulário
 * envia sempre o registro inteiro, e o schema de criação é o estrito — ele
 * exige nome, período e efeito coerente. Validar um `PATCH` completo com o
 * schema parcial deixaria passar exatamente os campos que o parcial tornou
 * opcionais.
 */

// ---------------------------------------------------------------------------
// As camadas
// ---------------------------------------------------------------------------

/**
 * As quatro camadas que esta tela edita, na ordem em que o motor as aplica.
 *
 * Os nomes são os do `TipoDeLinha` do motor (`satisfies` trava isso): é a mesma
 * palavra que aparece no detalhamento da cotação, e o rótulo sai de
 * `ROTULO_DO_TIPO_DE_LINHA` em vez de ser redigitado aqui. Quem lê "Dia da
 * semana" na linha do orçamento precisa achar "Dia da semana" nesta tela.
 *
 * A camada 1 (a tarifa da faixa) não está aqui porque não é regra: é a tabela.
 * O teto e o piso da categoria (`LIMITE`) também não — são campo da categoria,
 * na frota.
 */
export const CAMADAS_DE_REGRA = [
  'SAZONALIDADE',
  'DIA_DA_SEMANA',
  'ANTECEDENCIA',
  'OCUPACAO',
] as const satisfies readonly TipoDeLinha[]

export type CamadaDeRegra = (typeof CAMADAS_DE_REGRA)[number]

/**
 * "Todas as categorias" é exatamente o que a coluna de escopo da lista escreve
 * para uma regra sem categoria (`escopoDaRegra`). O rótulo da opção vazia sai da
 * MESMA função: a escolha feita aqui e a linha que aparece na tabela depois
 * precisam dizer a mesma palavra, senão parecem duas coisas diferentes.
 */
const TODAS_AS_CATEGORIAS = escopoDaRegra(null)

/** O par do anterior para o escopo de tabela, usado aqui e na lista de regras. */
export const QUALQUER_TABELA = 'Qualquer tabela'

export const ROTULO_DA_CAMADA: Readonly<Record<CamadaDeRegra, string>> = {
  SAZONALIDADE: ROTULO_DO_TIPO_DE_LINHA.SAZONALIDADE,
  DIA_DA_SEMANA: ROTULO_DO_TIPO_DE_LINHA.DIA_DA_SEMANA,
  ANTECEDENCIA: ROTULO_DO_TIPO_DE_LINHA.ANTECEDENCIA,
  OCUPACAO: ROTULO_DO_TIPO_DE_LINHA.OCUPACAO,
}

/**
 * O que o diálogo vai editar.
 *
 * União DISCRIMINADA pela camada, e não um par solto `(camada, regra)`: sem o
 * discriminante, nada impediria abrir a aba de antecedência passando uma
 * sazonalidade, e o formulário leria campos que não existem naquele objeto.
 * `regra` ausente = criação.
 */
export type AlvoDaRegra =
  | { readonly camada: 'SAZONALIDADE'; readonly regra?: Sazonalidade | undefined }
  | { readonly camada: 'DIA_DA_SEMANA'; readonly regra?: RegraDeDiaDaSemana | undefined }
  | { readonly camada: 'ANTECEDENCIA'; readonly regra?: RegraDeAntecedencia | undefined }
  | { readonly camada: 'OCUPACAO'; readonly regra?: RegraDeOcupacao | undefined }

// ---------------------------------------------------------------------------
// Conversões de pontos-base
// ---------------------------------------------------------------------------

/** Um multiplicador tem quatro casas de resolução: `PONTOS_BASE_NEUTROS` é 10⁴. */
const CASAS_DO_FATOR = 4

/** Percentual tem duas: 5000 pontos-base = 50%. */
const CASAS_DO_PERCENTUAL = 2

const PONTOS_BASE_POR_PONTO_PERCENTUAL = 100

/**
 * `1`, `1,25` ou `10.0001` — até dois dígitos inteiros (o teto é 10×) e quatro
 * casas.
 *
 * Escrita como ALTERNÂNCIA de dois padrões chatos em vez de um grupo opcional
 * aninhado: `eslint-plugin-security` mede altura de estrela e acusaria o `?` em
 * volta de um `{1,4}`. Aqui não haveria retrocesso de verdade, mas escrever a
 * regex que o plugin consegue provar segura é melhor do que silenciá-lo.
 */
const FATOR_DIGITADO = /^\d{1,2}$|^\d{1,2}[.,]\d{1,4}$/

const PERCENTUAL_DIGITADO = /^\d{1,3}$|^\d{1,3}[.,]\d{1,2}$/

const ZEROS_A_DIREITA = /0+$/

/**
 * `"1,25"` → `12500`; `"0,9"` → `9000`; `"1"` → `10000`. `null` quando não dá
 * para ler ou quando passa do teto de 10× — e `null` vira erro de campo, nunca
 * um multiplicador silencioso.
 *
 * A conta é sobre os DÍGITOS (parte inteira × 10⁴ + casas preenchidas à
 * direita), e não `Number(texto) * 10000`, pelo motivo do cabeçalho.
 */
function fatorParaPontosBase(texto: string): number | null {
  const limpo = texto.trim().replace('×', '').replace('x', '').trim()

  if (!FATOR_DIGITADO.test(limpo)) {
    return null
  }

  const [inteiro = '0', decimal = ''] = limpo.split(/[.,]/)
  const pontosBase =
    Number(inteiro) * PONTOS_BASE_NEUTROS + Number(decimal.padEnd(CASAS_DO_FATOR, '0'))

  return pontosBase > MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE ? null : pontosBase
}

/** `12500` → `"1,25"`; `10000` → `"1"`. O caminho de volta, para preencher o campo. */
function pontosBaseParaCampoDeFator(pontosBase: number): string {
  const inteiro = Math.trunc(pontosBase / PONTOS_BASE_NEUTROS)
  const casas = Math.abs(pontosBase % PONTOS_BASE_NEUTROS)

  if (casas === 0) {
    return String(inteiro)
  }

  // `2500` vira `25`; `0025` continua `0025` — só o zero à DIREITA é ruído.
  const decimal = String(casas).padStart(CASAS_DO_FATOR, '0').replace(ZEROS_A_DIREITA, '')

  return `${String(inteiro)},${decimal}`
}

/** `"15"` → `1500`; `"50"` → `5000`; `"12,5"` → `1250`. `null` acima de 100% ou ilegível. */
function percentualParaPontosBase(texto: string): number | null {
  const limpo = texto.trim().replace('%', '').trim()

  if (!PERCENTUAL_DIGITADO.test(limpo)) {
    return null
  }

  const [inteiro = '0', decimal = ''] = limpo.split(/[.,]/)
  const pontosBase =
    Number(inteiro) * PONTOS_BASE_POR_PONTO_PERCENTUAL +
    Number(decimal.padEnd(CASAS_DO_PERCENTUAL, '0'))

  return pontosBase > PONTOS_BASE_NEUTROS ? null : pontosBase
}

/** `1500` → `"15"`; `5000` → `"50"`; `1250` → `"12,5"`. O caminho de volta. */
function pontosBaseParaCampoDePercentual(pontosBase: number): string {
  const inteiro = Math.trunc(pontosBase / PONTOS_BASE_POR_PONTO_PERCENTUAL)
  const casas = Math.abs(pontosBase % PONTOS_BASE_POR_PONTO_PERCENTUAL)

  if (casas === 0) {
    return String(inteiro)
  }

  const decimal = String(casas).padStart(CASAS_DO_PERCENTUAL, '0').replace(ZEROS_A_DIREITA, '')

  return `${String(inteiro)},${decimal}`
}

/**
 * A conferência do percentual, embaixo do campo — a mesma ideia da dica do
 * multiplicador e pelo mesmo motivo: `0,7` aqui é 0,7% de ocupação, não 70%, e
 * a diferença de cem vezes só aparece se a tela disser o inteiro que vai viajar.
 */
function dicaDeOcupacao(digitado: string, padrao: string): string {
  const pontosBase = percentualParaPontosBase(digitado)

  if (pontosBase === null) {
    return padrao
  }

  return `${formatarPontosBaseComoPercentual(pontosBase)} · vai para a API como ${String(pontosBase)} pontos-base.`
}

const FATOR_INVALIDO = 'multiplicador inválido — use de 0 a 10, com até quatro casas (ex.: 1,25)'
const PERCENTUAL_INVALIDO = 'percentual inválido — use de 0 a 100 (ex.: 70)'
const VALOR_INVALIDO = 'valor inválido — use o formato 1.200,00'

function validarFator(valor: string): true | string {
  if (valor.trim().length === 0) {
    return 'informe o multiplicador'
  }

  return fatorParaPontosBase(valor) === null ? FATOR_INVALIDO : true
}

function validarPercentual(valor: string): true | string {
  if (valor.trim().length === 0) {
    return 'informe o percentual'
  }

  return percentualParaPontosBase(valor) === null ? PERCENTUAL_INVALIDO : true
}

/** Uma opção de escopo: o mínimo que o `<select>` precisa para existir. */
interface OpcaoDeEscopo {
  readonly id: string
  readonly nome: string
}

/**
 * A lista de um seletor de escopo COM o valor da regra em edição garantido
 * dentro dela.
 *
 * Não é enfeite: um `<select>` nativo a quem se atribui um valor sem `<option>`
 * correspondente fica com `selectedIndex = -1` e passa a exibir a primeira
 * opção — que aqui é "Todas as categorias". A tela então AFIRMA, em voz alta e
 * por escrito, que a regra vale para toda a frota, enquanto o estado do
 * formulário ainda guarda a categoria certa. Uma tela de preço que mostra um
 * escopo e salva outro é pior do que uma que recusa a abrir.
 *
 * As três situações em que isso acontece são todas reais: a consulta de
 * categorias ainda não voltou quando o diálogo abriu, a consulta falhou, ou a
 * categoria foi DESATIVADA depois de a regra ser criada — `useCategoriasAtivas`
 * lista só as ativas, e a regra antiga continua apontando para ela.
 */
function comOEscopoAtual(
  lista: readonly OpcaoDeEscopo[],
  atual: OpcaoDeEscopo | null,
): readonly OpcaoDeEscopo[] {
  if (atual === null || lista.some((opcao) => opcao.id === atual.id)) {
    return lista
  }

  return [...lista, atual]
}

/** `0` → `Domingo`. O rótulo é o da frota — a semana é a mesma em todo o painel. */
function rotuloDoDia(dia: number): string {
  const chave = DIAS_DA_SEMANA[dia]

  return chave === undefined ? SEM_VALOR : ROTULO_DO_DIA[chave]
}

// ---------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------

/**
 * Os campos cujo nome COINCIDE com o caminho do schema — só esses viram erro de
 * campo. Multiplicador e faixa de ocupação chegam do Zod como
 * `multiplicadorEmPontosBase` e `ocupacaoMinimaEmPontosBase`, que não são
 * campos deste formulário (aqui eles são fator e percentual): caem no alerta do
 * topo. Na prática o Zod quase nunca os alcança — a conversão local recusa
 * antes, com a mensagem certa no campo certo.
 */
const CAMPOS = [
  'nome',
  'categoryId',
  'priceTableId',
  'inicio',
  'fim',
  'efeito',
  'valorFixo',
  'prioridade',
  'diasDaSemana',
  'minimoEmDias',
  'maximoEmDias',
  'ativo',
] as const

interface FormularioDeRegra {
  nome: string
  /** `''` = todas as categorias. */
  categoryId: string
  /** `''` = qualquer tabela de preço. Só a sazonalidade tem escopo de tabela. */
  priceTableId: string
  inicio: string
  fim: string
  efeito: EfeitoDeRegra
  /** Reais digitados (`"1.200,00"`), convertidos para centavos no envio. */
  valorFixo: string
  /** FATOR digitado (`"1,25"`), convertido para pontos-base no envio. */
  multiplicador: string
  prioridade: string
  /** `'0'`…`'6'` — o que um grupo de checkboxes do react-hook-form devolve. */
  diasDaSemana: string[]
  minimoEmDias: string
  maximoEmDias: string
  /** PERCENTUAL digitado (`"40"`), convertido para pontos-base no envio. */
  ocupacaoMinima: string
  ocupacaoMaxima: string
  ativo: boolean
}

const FORMULARIO_VAZIO: FormularioDeRegra = {
  nome: '',
  categoryId: '',
  priceTableId: '',
  inicio: '',
  fim: '',
  efeito: 'MULTIPLICADOR',
  valorFixo: '',
  multiplicador: '',
  prioridade: '0',
  diasDaSemana: [],
  minimoEmDias: '',
  maximoEmDias: '',
  ocupacaoMinima: '',
  ocupacaoMaxima: '',
  ativo: true,
}

function valoresIniciais(alvo: AlvoDaRegra): FormularioDeRegra {
  switch (alvo.camada) {
    case 'SAZONALIDADE': {
      const regra = alvo.regra

      if (regra === undefined) {
        return FORMULARIO_VAZIO
      }

      return {
        ...FORMULARIO_VAZIO,
        nome: regra.nome,
        categoryId: regra.categoryId ?? '',
        priceTableId: regra.priceTableId ?? '',
        // `<input type="date">` ignora em silêncio um ISO com hora: o campo
        // ficaria vazio e ninguém saberia por quê.
        inicio: regra.inicio.slice(0, 10),
        fim: regra.fim.slice(0, 10),
        efeito: regra.efeito,
        valorFixo: paraCampoDeReais(regra.valorFixo),
        multiplicador: pontosBaseParaCampoDeFator(regra.multiplicadorEmPontosBase),
        prioridade: String(regra.prioridade),
        ativo: regra.ativo,
      }
    }

    case 'DIA_DA_SEMANA': {
      const regra = alvo.regra

      if (regra === undefined) {
        return FORMULARIO_VAZIO
      }

      return {
        ...FORMULARIO_VAZIO,
        nome: regra.nome,
        categoryId: regra.categoryId ?? '',
        diasDaSemana: regra.diasDaSemana.map(String),
        multiplicador: pontosBaseParaCampoDeFator(regra.multiplicadorEmPontosBase),
        ativo: regra.ativo,
      }
    }

    case 'ANTECEDENCIA': {
      const regra = alvo.regra

      if (regra === undefined) {
        return FORMULARIO_VAZIO
      }

      return {
        ...FORMULARIO_VAZIO,
        nome: regra.nome,
        categoryId: regra.categoryId ?? '',
        minimoEmDias: String(regra.minimoEmDias),
        // Vazio é o "de 30 dias em diante", e não um teto esquecido.
        maximoEmDias: regra.maximoEmDias === null ? '' : String(regra.maximoEmDias),
        multiplicador: pontosBaseParaCampoDeFator(regra.multiplicadorEmPontosBase),
        ativo: regra.ativo,
      }
    }

    case 'OCUPACAO': {
      const regra = alvo.regra

      if (regra === undefined) {
        return FORMULARIO_VAZIO
      }

      return {
        ...FORMULARIO_VAZIO,
        nome: regra.nome,
        categoryId: regra.categoryId ?? '',
        ocupacaoMinima: pontosBaseParaCampoDePercentual(regra.ocupacaoMinimaEmPontosBase),
        ocupacaoMaxima: pontosBaseParaCampoDePercentual(regra.ocupacaoMaximaEmPontosBase),
        multiplicador: pontosBaseParaCampoDeFator(regra.multiplicadorEmPontosBase),
        ativo: regra.ativo,
      }
    }
  }
}

/** O que cada camada recorta, dito na abertura do formulário. */
const DESCRICAO_DA_CAMADA: Readonly<Record<CamadaDeRegra, string>> = {
  SAZONALIDADE:
    'Um período nomeado com efeito no preço: alta temporada, feriado, Fórmula 1. Duas regras podem cobrir o mesmo dia — vence a de maior prioridade.',
  DIA_DA_SEMANA:
    'Multiplicador por dia da semana. Ele incide sobre o que a sazonalidade já produziu naquele dia — as duas camadas compõem.',
  ANTECEDENCIA:
    'Early bird e last minute na mesma lista: o que muda é a distância entre a cotação e a retirada. Entre duas faixas que cobrem a mesma antecedência, vence a mais estreita.',
  OCUPACAO:
    'Multiplicador por faixa de ocupação prevista da categoria. Só vale com o preço por ocupação ligado na política de locação.',
}

export interface DialogoDeRegraProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** A camada e, quando é edição, a regra. */
  readonly alvo: AlvoDaRegra
}

export function DialogoDeRegra({ aberto, aoFechar, alvo }: DialogoDeRegraProps): ReactElement {
  const editando = alvo.regra !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const categorias = useCategoriasAtivas()
  const tabelas = useTabelasAtivas()
  const politica = usePoliticaDeLocacao()

  const criarSazonalidade = useCriarSazonalidade()
  const atualizarSazonalidade = useAtualizarSazonalidade()
  const criarDiaDaSemana = useCriarRegraDeDiaDaSemana()
  const atualizarDiaDaSemana = useAtualizarRegraDeDiaDaSemana()
  const criarAntecedencia = useCriarRegraDeAntecedencia()
  const atualizarAntecedencia = useAtualizarRegraDeAntecedencia()
  const criarOcupacao = useCriarRegraDeOcupacao()
  const atualizarOcupacao = useAtualizarRegraDeOcupacao()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDeRegra>({ defaultValues: FORMULARIO_VAZIO })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, abrir "nova
  // regra" depois de "editar" mostraria os valores da regra anterior — e salvar
  // criaria uma cópia dela com outro nome.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciais(alvo))
    }
  }, [aberto, alvo, reset])

  const efeito = watch('efeito')
  const multiplicadorDigitado = watch('multiplicador')
  const ocupacaoMinimaDigitada = watch('ocupacaoMinima')
  const ocupacaoMaximaDigitada = watch('ocupacaoMaxima')

  const ocupado =
    criarSazonalidade.isPending ||
    atualizarSazonalidade.isPending ||
    criarDiaDaSemana.isPending ||
    atualizarDiaDaSemana.isPending ||
    criarAntecedencia.isPending ||
    atualizarAntecedencia.isPending ||
    criarOcupacao.isPending ||
    atualizarOcupacao.isPending

  /** Em `FIXO` o multiplicador é ignorado pelo motor — e some da tela. */
  const usaMultiplicador = alvo.camada !== 'SAZONALIDADE' || efeito === 'MULTIPLICADOR'

  /**
   * O número exato que vai para a API, embaixo do campo.
   *
   * É esta linha que pega o fator 100 que a validação NÃO pega, porque ele é um
   * número legítimo: quem digita `10` querendo "1,0" lê "10× · +900% · vai para
   * a API como 100000 pontos-base" e corrige antes de salvar — o teto de 10×
   * aceitaria a diária de R$ 9.000 sem reclamar. Os erros grosseiros (`125`,
   * `12,5`) morrem antes, em `validarFator`.
   */
  const pontosBaseDigitados = fatorParaPontosBase(multiplicadorDigitado)
  const dicaDoMultiplicador =
    pontosBaseDigitados === null
      ? 'Fator sobre a diária da faixa: 1,25 encarece 25%; 0,9 dá 10% de desconto; 1 não mexe no preço.'
      : `${formatarMultiplicador(pontosBaseDigitados)} · ${formatarVariacaoDoMultiplicador(pontosBaseDigitados)} · vai para a API como ${String(pontosBaseDigitados)} pontos-base.`

  /**
   * A categoria da regra vem do próprio registro (`categoria`), e não de uma
   * busca na lista: é ela que sobrevive quando a lista não veio ou quando a
   * categoria foi desativada.
   */
  const categoriaDaRegra = alvo.regra?.categoria ?? null

  const opcoesDeCategoria = useMemo(
    () =>
      comOEscopoAtual(
        (categorias.data?.itens ?? []).map((categoria) => ({
          id: categoria.id,
          nome: categoria.nome,
        })),
        categoriaDaRegra,
      ),
    [categorias.data, categoriaDaRegra],
  )

  // A sazonalidade guarda só o `priceTableId` — sem o nome da tabela, o rótulo
  // da opção de resgate diz o que ela é em vez de fingir um nome.
  const idDaTabelaDaRegra =
    alvo.camada === 'SAZONALIDADE' ? (alvo.regra?.priceTableId ?? null) : null

  const opcoesDeTabela = useMemo(
    () =>
      comOEscopoAtual(
        (tabelas.data?.itens ?? []).map((tabela) => ({ id: tabela.id, nome: tabela.nome })),
        idDaTabelaDaRegra === null ? null : { id: idDaTabelaDaRegra, nome: 'Tabela desta regra' },
      ),
    [tabelas.data, idDaTabelaDaRegra],
  )

  /**
   * Consulta que falhou vira frase, e não um seletor desabilitado e mudo — que
   * é indistinguível de tela travada. O escopo já escolhido continua no lugar.
   */
  const dicaDaCategoria = categorias.isError
    ? 'Não foi possível carregar as categorias. O escopo atual da regra está preservado e não muda ao salvar.'
    : 'Vazio vale para TODAS as categorias — é o padrão.'

  const dicaDaTabela = tabelas.isError
    ? 'Não foi possível carregar as tabelas de preço. O escopo atual da regra está preservado e não muda ao salvar.'
    : 'Vazio vale em qualquer tabela, inclusive nas negociadas com clientes corporativos.'

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const opcoes = {
      onSuccess: () => {
        toast.success(editando ? 'Regra atualizada' : 'Regra criada', {
          description: `${ROTULO_DA_CAMADA[alvo.camada]} · ${valores.nome}. O preço muda da próxima cotação em diante — orçamento já emitido continua valendo o que valia.`,
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    }

    // Escopo vazio vai como `null` EXPLÍCITO, e não ausente: `undefined` diria
    // "não mexa" no PATCH, e trocar uma regra de categoria para "todas" ficaria
    // sem efeito, calado.
    const categoryId = valores.categoryId.length > 0 ? valores.categoryId : null

    switch (alvo.camada) {
      case 'SAZONALIDADE': {
        // O efeito vem dos VALORES enviados, e não do `watch` do render: são o
        // mesmo valor, e depender do render aqui é o tipo de acoplamento que
        // deixa de ser verdade quando o formulário ganhar um reset assíncrono.
        const fixo = valores.efeito === 'FIXO'
        const valorFixo = fixo ? deCampoDeReaisParaWire(valores.valorFixo) : null

        if (fixo && valorFixo === null) {
          setError('valorFixo', { type: 'validate', message: VALOR_INVALIDO })
          return
        }

        // Em `FIXO` o multiplicador não é lido pelo motor; gravar o neutro
        // impede que um fator antigo reapareça se alguém voltar o efeito para
        // multiplicador meses depois.
        const multiplicador = fixo
          ? PONTOS_BASE_NEUTROS
          : fatorParaPontosBase(valores.multiplicador)

        if (multiplicador === null) {
          setError('multiplicador', { type: 'validate', message: FATOR_INVALIDO })
          return
        }

        const entrada = {
          nome: valores.nome,
          categoryId,
          priceTableId: valores.priceTableId.length > 0 ? valores.priceTableId : null,
          inicio: valores.inicio,
          fim: valores.fim,
          efeito: valores.efeito,
          valorFixo,
          multiplicadorEmPontosBase: multiplicador,
          prioridade: valores.prioridade,
          ativo: valores.ativo,
        }

        const validado = criarSazonalidadeSchema.safeParse(entrada)

        if (!validado.success) {
          setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
          return
        }

        // `entrada`, e não `validado.data`: o schema transforma `valorFixo` em
        // `bigint`, e `JSON.stringify` de um `bigint` LANÇA.
        if (alvo.regra === undefined) {
          criarSazonalidade.mutate(entrada, opcoes)
        } else {
          atualizarSazonalidade.mutate({ id: alvo.regra.id, dados: entrada }, opcoes)
        }

        return
      }

      case 'DIA_DA_SEMANA': {
        const multiplicador = fatorParaPontosBase(valores.multiplicador)

        if (multiplicador === null) {
          setError('multiplicador', { type: 'validate', message: FATOR_INVALIDO })
          return
        }

        const entrada = {
          nome: valores.nome,
          categoryId,
          diasDaSemana: valores.diasDaSemana,
          multiplicadorEmPontosBase: multiplicador,
          ativo: valores.ativo,
        }

        const validado = criarRegraDeDiaDaSemanaSchema.safeParse(entrada)

        if (!validado.success) {
          setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
          return
        }

        if (alvo.regra === undefined) {
          criarDiaDaSemana.mutate(entrada, opcoes)
        } else {
          atualizarDiaDaSemana.mutate({ id: alvo.regra.id, dados: entrada }, opcoes)
        }

        return
      }

      case 'ANTECEDENCIA': {
        const multiplicador = fatorParaPontosBase(valores.multiplicador)

        if (multiplicador === null) {
          setError('multiplicador', { type: 'validate', message: FATOR_INVALIDO })
          return
        }

        const entrada = {
          nome: valores.nome,
          categoryId,
          minimoEmDias: valores.minimoEmDias,
          // Vazio é "sem teto", e precisa viajar como `null`: string vazia num
          // `z.coerce.number` vira ZERO, e a faixa "de 30 dias em diante"
          // silenciosamente viraria "de 30 a 0 dias".
          maximoEmDias: valores.maximoEmDias.trim().length > 0 ? valores.maximoEmDias : null,
          multiplicadorEmPontosBase: multiplicador,
          ativo: valores.ativo,
        }

        const validado = criarRegraDeAntecedenciaSchema.safeParse(entrada)

        if (!validado.success) {
          setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
          return
        }

        if (alvo.regra === undefined) {
          criarAntecedencia.mutate(entrada, opcoes)
        } else {
          atualizarAntecedencia.mutate({ id: alvo.regra.id, dados: entrada }, opcoes)
        }

        return
      }

      case 'OCUPACAO': {
        const multiplicador = fatorParaPontosBase(valores.multiplicador)

        if (multiplicador === null) {
          setError('multiplicador', { type: 'validate', message: FATOR_INVALIDO })
          return
        }

        const minima = percentualParaPontosBase(valores.ocupacaoMinima)
        const maxima = percentualParaPontosBase(valores.ocupacaoMaxima)

        if (minima === null) {
          setError('ocupacaoMinima', { type: 'validate', message: PERCENTUAL_INVALIDO })
          return
        }

        if (maxima === null) {
          setError('ocupacaoMaxima', { type: 'validate', message: PERCENTUAL_INVALIDO })
          return
        }

        const entrada = {
          nome: valores.nome,
          categoryId,
          ocupacaoMinimaEmPontosBase: minima,
          ocupacaoMaximaEmPontosBase: maxima,
          multiplicadorEmPontosBase: multiplicador,
          ativo: valores.ativo,
        }

        const validado = criarRegraDeOcupacaoSchema.safeParse(entrada)

        if (!validado.success) {
          setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
          return
        }

        if (alvo.regra === undefined) {
          criarOcupacao.mutate(entrada, opcoes)
        } else {
          atualizarOcupacao.mutate({ id: alvo.regra.id, dados: entrada }, opcoes)
        }

        return
      }
    }
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editando ? 'Editar regra' : 'Nova regra'} · {ROTULO_DA_CAMADA[alvo.camada]}
          </DialogTitle>
          <DialogDescription>{DESCRICAO_DA_CAMADA[alvo.camada]}</DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          {/* Cadastrar regra de ocupação com o interruptor fechado é legítimo —
              é assim que se prepara a virada. Deixar de dizer que ela não vale
              é que não é: a pessoa salva, testa uma cotação e conclui que o
              sistema está quebrado. */}
          {alvo.camada === 'OCUPACAO' && politica.data?.precoPorOcupacaoAtivo === false ? (
            <Alert variant="warning">
              <AlertTitle>O preço por ocupação está desligado</AlertTitle>
              <AlertDescription>
                Esta regra fica guardada, mas o motor não a consulta enquanto ninguém ligar o preço
                por ocupação na política de locação. Nenhuma cotação muda por causa dela hoje.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Nome da regra"
              obrigatorio
              erro={errors.nome?.message}
              dica="É este nome que aparece no detalhamento do orçamento do cliente."
              className="sm:col-span-2"
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('nome', { required: 'informe o nome da regra' })}
                  placeholder="Alta temporada de verão"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Categoria" erro={errors.categoryId?.message} dica={dicaDaCategoria}>
              {(campo) => (
                <Select
                  {...campo}
                  {...register('categoryId')}
                  disabled={ocupado || categorias.isPending || categorias.isError}
                >
                  <option value="">
                    {categorias.isPending ? 'Carregando…' : TODAS_AS_CATEGORIAS}
                  </option>
                  {opcoesDeCategoria.map((categoria) => (
                    <option key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            {alvo.camada === 'SAZONALIDADE' ? (
              <Campo
                rotulo="Tabela de preço"
                erro={errors.priceTableId?.message}
                dica={dicaDaTabela}
              >
                {(campo) => (
                  <Select
                    {...campo}
                    {...register('priceTableId')}
                    disabled={ocupado || tabelas.isPending || tabelas.isError}
                  >
                    <option value="">{tabelas.isPending ? 'Carregando…' : QUALQUER_TABELA}</option>
                    {opcoesDeTabela.map((tabela) => (
                      <option key={tabela.id} value={tabela.id}>
                        {tabela.nome}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>
            ) : null}
          </div>

          {alvo.camada === 'SAZONALIDADE' ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo rotulo="Primeiro dia" obrigatorio erro={errors.inicio?.message}>
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('inicio', { required: 'informe o primeiro dia' })}
                      type="date"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>

                <Campo
                  rotulo="Último dia"
                  obrigatorio
                  erro={errors.fim?.message}
                  dica="Inclusive: uma regra de 20 a 27 pega o dia 27 inteiro."
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('fim', { required: 'informe o último dia' })}
                      type="date"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>
              </div>

              <fieldset className="space-y-2" disabled={ocupado}>
                <legend className="text-sm font-medium text-foreground">Efeito no preço</legend>

                <div
                  role="radiogroup"
                  aria-label="Efeito no preço"
                  className="grid gap-2 sm:grid-cols-2"
                >
                  {EFEITOS_DE_REGRA.map((opcao) => {
                    const selecionado = efeito === opcao
                    const Icone = opcao === 'FIXO' ? Banknote : Percent

                    return (
                      <button
                        key={opcao}
                        type="button"
                        role="radio"
                        aria-checked={selecionado}
                        disabled={ocupado}
                        onClick={() => {
                          setValue('efeito', opcao)
                        }}
                        className={cn(
                          'flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-sm',
                          'transition-colors focus-visible:outline-none focus-visible:ring-2',
                          'focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70',
                          // A marca marca a ESCOLHA (borda e tinta de fundo), não
                          // o texto: `text-accent-foreground` pintaria de amarelo
                          // as duas linhas do cartão, e a tela ganharia um bloco
                          // de texto amarelo do tamanho de um parágrafo.
                          selecionado
                            ? 'border-primary bg-accent text-foreground'
                            : 'border-border bg-surface text-fg-secondary hover:bg-elevated',
                        )}
                      >
                        <Icone className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                        <span className="min-w-0">
                          <span className="block font-medium">{ROTULO_DO_EFEITO[opcao]}</span>
                          <span className="block text-xs">
                            {opcao === 'FIXO'
                              ? 'SUBSTITUI a diária da faixa pelo valor que você digitar.'
                              : 'ESCALA a diária da faixa — 1,25× sobre R$ 900 dá R$ 1.125.'}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>

                <p className="text-xs text-fg-muted">
                  A escolha muda o preço em ordem de grandeza: 1200 num campo de multiplicador é
                  1.200 vezes a diária, e 1,25 num campo de valor fixo é uma diária de R$ 1,25.
                </p>

                {/* `efeito` está em `CAMPOS`, então a API e o Zod conseguem
                    apontar o erro para cá — sem esta linha ele não apareceria em
                    lugar nenhum, que é o pior desfecho de uma validação. */}
                {errors.efeito?.message === undefined ? null : (
                  <p className="text-xs text-danger-fg">{errors.efeito.message}</p>
                )}
              </fieldset>

              <div className="grid gap-4 sm:grid-cols-2">
                {efeito === 'FIXO' ? (
                  <Campo
                    rotulo="Diária no período"
                    obrigatorio
                    erro={errors.valorFixo?.message}
                    dica="Em reais. É a diária cheia — a tarifa da faixa é descartada nestes dias."
                  >
                    {(campo) => (
                      <Input
                        {...campo}
                        {...register('valorFixo', {
                          validate: (valor: string) =>
                            deCampoDeReaisParaWire(valor) !== null || VALOR_INVALIDO,
                        })}
                        inputMode="decimal"
                        placeholder="1.200,00"
                        className="tabular-money"
                        disabled={ocupado}
                      />
                    )}
                  </Campo>
                ) : null}

                <Campo
                  rotulo="Prioridade"
                  obrigatorio
                  erro={errors.prioridade?.message}
                  dica={`De 0 a ${String(PRIORIDADE_MAXIMA)}. Quando duas regras cobrem o mesmo dia, vence a de MAIOR prioridade; empatadas, vence a de período mais curto.`}
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('prioridade', { required: 'informe a prioridade' })}
                      type="number"
                      min={0}
                      max={PRIORIDADE_MAXIMA}
                      inputMode="numeric"
                      placeholder="0"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>
              </div>
            </>
          ) : null}

          {alvo.camada === 'DIA_DA_SEMANA' ? (
            <fieldset className="space-y-2" disabled={ocupado}>
              <legend className="text-sm font-medium text-foreground">Dias da semana</legend>
              <p className="text-xs text-fg-muted">
                O multiplicador vale nos dias marcados. Duas regras ativas que marcam o mesmo dia
                não se somam — o motor aplica uma só, e qual delas não é escolha sua.
              </p>

              <div className="grid gap-2 sm:grid-cols-2">
                {DIAS_DA_SEMANA_VALIDOS.map((dia) => (
                  <label
                    key={dia}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border
                      border-border bg-surface p-3 text-sm"
                  >
                    <Checkbox
                      value={String(dia)}
                      {...register('diasDaSemana', {
                        validate: (valor: string[]) =>
                          valor.length > 0 || 'escolha ao menos um dia da semana',
                      })}
                      disabled={ocupado}
                    />
                    <span className="font-medium text-foreground">{rotuloDoDia(dia)}</span>
                  </label>
                ))}
              </div>

              {errors.diasDaSemana?.message === undefined ? null : (
                <p className="text-xs text-danger-fg">{errors.diasDaSemana.message}</p>
              )}
            </fieldset>
          ) : null}

          {alvo.camada === 'ANTECEDENCIA' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="A partir de"
                obrigatorio
                erro={errors.minimoEmDias?.message}
                dica="Em dias entre a cotação e a retirada. 0 é a locação para hoje."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('minimoEmDias', { required: 'informe a antecedência mínima' })}
                    type="number"
                    min={0}
                    max={ANTECEDENCIA_MAXIMA_EM_DIAS}
                    inputMode="numeric"
                    placeholder="0"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Até"
                erro={errors.maximoEmDias?.message}
                dica="Vazio é o early bird “de tantos dias em diante”, sem teto."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('maximoEmDias')}
                    type="number"
                    min={0}
                    max={ANTECEDENCIA_MAXIMA_EM_DIAS}
                    inputMode="numeric"
                    placeholder="sem teto"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          ) : null}

          {alvo.camada === 'OCUPACAO' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Ocupação a partir de"
                obrigatorio
                erro={errors.ocupacaoMinima?.message}
                dica={dicaDeOcupacao(
                  ocupacaoMinimaDigitada,
                  'Em % da capacidade prevista da categoria no período.',
                )}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('ocupacaoMinima', { validate: validarPercentual })}
                    inputMode="decimal"
                    placeholder="70"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Ocupação até"
                obrigatorio
                erro={errors.ocupacaoMaxima?.message}
                // Duas faixas que se cruzam NÃO são desempatadas por mínimo nem
                // por largura: o motor aplica a primeira que casa na ordem em que
                // o banco devolveu as linhas. Dizer "vence a de menor mínimo"
                // seria ensinar uma regra de resolução que não existe.
                dica={dicaDeOcupacao(
                  ocupacaoMaximaDigitada,
                  'Evite cruzar faixas: na sobreposição o motor aplica só uma, e qual delas não é escolha sua.',
                )}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('ocupacaoMaxima', { validate: validarPercentual })}
                    inputMode="decimal"
                    placeholder="100"
                    className="tabular-money"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          ) : null}

          {usaMultiplicador ? (
            <Campo
              rotulo="Multiplicador"
              obrigatorio
              erro={errors.multiplicador?.message}
              dica={dicaDoMultiplicador}
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('multiplicador', { validate: validarFator })}
                  inputMode="decimal"
                  placeholder="1,25"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          ) : null}

          <label
            className="flex cursor-pointer items-start gap-2 rounded-lg border border-border
              bg-surface p-3 text-sm"
          >
            <Checkbox {...register('ativo')} className="mt-0.5" disabled={ocupado} />
            <span className="min-w-0">
              <span className="block font-medium text-foreground">Regra em vigor</span>
              <span className="block text-xs text-fg-secondary">
                Desmarcada, a regra fica guardada e o motor não a consulta. Cotação já emitida não
                muda — o preço dela foi congelado no momento em que saiu.
              </span>
            </span>
          </label>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Criar regra'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
